import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { FileText, Save, Printer, Trash2, FolderOpen, RefreshCw } from 'lucide-react';
import { printFormatsApi } from '../services/printFormatsApi';

const PrintFormatDesigner = () => {
  const [doctypes, setDoctypes] = useState([]);
  const [doctype, setDoctype] = useState('');
  const [fields, setFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [sampleDoc, setSampleDoc] = useState({});
  const [formatName, setFormatName] = useState('');
  const [title, setTitle] = useState('');
  const [pageSize, setPageSize] = useState('A4');
  const [orientation, setOrientation] = useState('portrait');
  const [customHtml, setCustomHtml] = useState('');
  const [customCss, setCustomCss] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [saved, setSaved] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    printFormatsApi.doctypes().then((r) => setDoctypes(r.data || [])).catch(() => toast.error('Failed to load doctypes'));
    loadSaved();
  }, []);

  const loadSaved = () => printFormatsApi.list().then((r) => setSaved(r.data || [])).catch(() => {});

  const buildFormat = useCallback(() => ({
    doctype,
    name: formatName || 'Untitled',
    title: title || formatName || 'Document',
    fields: selectedFields.map((fn) => {
      const meta = fields.find((f) => f.fieldname === fn);
      return { fieldname: fn, label: meta?.label || fn };
    }),
    custom_html: customHtml || null,
    css: customCss || null,
    page_size: pageSize,
    orientation,
    active: true,
  }), [doctype, formatName, title, selectedFields, fields, customHtml, customCss, pageSize, orientation]);

  // Live preview (debounced)
  useEffect(() => {
    if (!doctype) { setPreviewHtml(''); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await printFormatsApi.render({ format: buildFormat(), doc: sampleDoc });
        setPreviewHtml(r.data.html);
      } catch {
        /* ignore transient preview errors */
      }
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [doctype, sampleDoc, buildFormat]);

  const handleDoctypeChange = async (dt) => {
    setDoctype(dt);
    setActiveId(null);
    try {
      const [f, s] = await Promise.all([printFormatsApi.fields(dt), printFormatsApi.sample(dt)]);
      setFields(f.data || []);
      setSelectedFields((f.data || []).slice(0, 8).map((x) => x.fieldname));
      setSampleDoc(s.data || {});
    } catch {
      toast.error('Failed to load fields');
    }
  };

  const toggleField = (fn) =>
    setSelectedFields((prev) => (prev.includes(fn) ? prev.filter((c) => c !== fn) : [...prev, fn]));

  const saveFormat = async () => {
    if (!doctype) { toast.error('Select a DocType'); return; }
    if (!formatName.trim()) { toast.error('Enter a format name'); return; }
    try {
      if (activeId) {
        await printFormatsApi.update(activeId, buildFormat());
        toast.success('Print format updated');
      } else {
        const r = await printFormatsApi.create(buildFormat());
        setActiveId(r.data.print_format_id);
        toast.success('Print format saved');
      }
      loadSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const loadFormat = async (pf) => {
    setActiveId(pf.print_format_id);
    setFormatName(pf.name);
    setTitle(pf.title || '');
    setPageSize(pf.page_size || 'A4');
    setOrientation(pf.orientation || 'portrait');
    setCustomHtml(pf.custom_html || '');
    setCustomCss(pf.css || '');
    setDoctype(pf.doctype);
    try {
      const [f, s] = await Promise.all([printFormatsApi.fields(pf.doctype), printFormatsApi.sample(pf.doctype)]);
      setFields(f.data || []);
      setSampleDoc(s.data || {});
      setSelectedFields((pf.fields || []).map((x) => x.fieldname));
    } catch {
      toast.error('Could not load this format’s DocType');
    }
  };

  const deleteFormat = async (id, e) => {
    e.stopPropagation();
    try {
      await printFormatsApi.remove(id);
      toast.success('Deleted');
      if (id === activeId) { setActiveId(null); setFormatName(''); }
      loadSaved();
    } catch { toast.error('Failed to delete'); }
  };

  const doPrint = () => {
    if (!previewHtml) { toast.error('Nothing to print'); return; }
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blocked — allow popups to print'); return; }
    w.document.write(previewHtml);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <Layout>
      <div className="p-6 max-w-[1600px] mx-auto" data-testid="print-format-designer">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Print Format Designer</h1>
              <p className="text-sm text-gray-500">Design printable layouts for any DocType</p>
            </div>
          </div>
          <Button variant="outline" onClick={loadSaved}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Builder panel */}
          <Card className="lg:col-span-2 p-4 space-y-4 h-fit" data-testid="pf-builder-panel">
            <div>
              <Label className="text-xs font-semibold">DocType</Label>
              <Select value={doctype} onValueChange={handleDoctypeChange}>
                <SelectTrigger data-testid="pf-doctype-select"><SelectValue placeholder="Select a DocType" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {doctypes.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold">Format Name</Label>
                <Input className="h-8 text-sm" value={formatName} onChange={(e) => setFormatName(e.target.value)} placeholder="e.g. Standard SO" data-testid="pf-name-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Print Title</Label>
                <Input className="h-8 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Heading on printout" data-testid="pf-title-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold">Page Size</Label>
                <Select value={pageSize} onValueChange={setPageSize}>
                  <SelectTrigger className="h-8 text-xs" data-testid="pf-pagesize-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['A4', 'Letter', 'Legal'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Orientation</Label>
                <Select value={orientation} onValueChange={setOrientation}>
                  <SelectTrigger className="h-8 text-xs" data-testid="pf-orientation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait</SelectItem>
                    <SelectItem value="landscape">Landscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {fields.length > 0 && (
              <div>
                <Label className="text-xs font-semibold">Fields to include</Label>
                <div className="mt-1 max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                  {fields.map((f) => (
                    <label key={f.fieldname} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                      <Checkbox
                        checked={selectedFields.includes(f.fieldname)}
                        onCheckedChange={() => toggleField(f.fieldname)}
                        data-testid={`pf-field-${f.fieldname}`}
                      />
                      <span className="truncate">{f.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold">Custom HTML (optional)</Label>
              <Textarea
                className="font-mono text-xs h-28"
                value={customHtml}
                onChange={(e) => setCustomHtml(e.target.value)}
                placeholder="Leave blank for the standard layout. Tokens: {{ _title }}, {{ _fields_table }}, {{ fieldname }}"
                data-testid="pf-html-textarea"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Tokens: <code>{'{{ _title }}'}</code>, <code>{'{{ _fields_table }}'}</code>, <code>{'{{ fieldname }}'}</code>
              </p>
            </div>

            <div>
              <Label className="text-xs font-semibold">Custom CSS (optional)</Label>
              <Textarea
                className="font-mono text-xs h-20"
                value={customCss}
                onChange={(e) => setCustomCss(e.target.value)}
                placeholder=".pf-title { color: #9a3412; }"
                data-testid="pf-css-textarea"
              />
            </div>

            <div className="flex gap-2 border-t pt-3">
              <Button className="flex-1 bg-orange-600 hover:bg-orange-700" onClick={saveFormat} data-testid="pf-save-btn">
                <Save className="w-4 h-4 mr-1" />{activeId ? 'Update' : 'Save'}
              </Button>
              <Button variant="outline" className="flex-1" onClick={doPrint} data-testid="pf-print-btn">
                <Printer className="w-4 h-4 mr-1" />Print
              </Button>
            </div>
          </Card>

          {/* Preview + saved */}
          <div className="lg:col-span-3 space-y-6">
            {saved.length > 0 && (
              <Card className="p-4">
                <Label className="text-xs font-semibold flex items-center gap-1 mb-2"><FolderOpen className="w-3.5 h-3.5" />Saved Formats</Label>
                <div className="flex flex-wrap gap-2">
                  {saved.map((pf) => (
                    <button
                      key={pf.print_format_id}
                      onClick={() => loadFormat(pf)}
                      data-testid={`saved-pf-${pf.print_format_id}`}
                      className={`group flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${activeId === pf.print_format_id ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-orange-300'}`}
                    >
                      {pf.name} <span className="text-xs text-gray-400">({pf.doctype})</span>
                      <Trash2 className="w-3.5 h-3.5 text-gray-300 group-hover:text-red-500" onClick={(e) => deleteFormat(pf.print_format_id, e)} />
                    </button>
                  ))}
                </div>
              </Card>
            )}

            <Card className="p-5" data-testid="pf-preview-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800">Live Preview</h3>
                <span className="text-xs text-gray-400">{pageSize} · {orientation}</span>
              </div>
              {previewHtml ? (
                <iframe
                  title="print-preview"
                  data-testid="pf-preview-iframe"
                  srcDoc={previewHtml}
                  className="w-full h-[560px] border rounded-md bg-white"
                />
              ) : (
                <div className="h-[560px] flex flex-col items-center justify-center text-gray-400 border rounded-md">
                  <FileText className="w-10 h-10 mb-2" />
                  <p className="text-sm">Select a DocType to start designing</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrintFormatDesigner;
